
import React from "react";
import { Site } from "@/services/types";
import SiteCard from "./SiteCard";

interface SitesListProps {
  sites: Site[];
  loading: boolean;
}

const SitesList = ({ sites, loading }: SitesListProps) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]" role="status" aria-label="Chargement des sites">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-fabre-green border-t-transparent mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold text-fabre-gray mb-2">Chargement de nos sites</h3>
          <p className="text-fabre-darkteal">Localisation de nos centres en cours...</p>
        </div>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="max-w-lg mx-auto">
          <div className="w-32 h-32 bg-gradient-to-br from-fabre-lightgray to-fabre-beige rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <svg className="w-16 h-16 text-fabre-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-2xl font-heading font-bold text-fabre-gray mb-4">
            Aucun site trouvé dans cette zone
          </h3>
          <p className="text-lg text-fabre-darkteal mb-6">
            Nous n'avons actuellement aucun centre dans cette région, mais nous étudions constamment de nouvelles implantations.
          </p>
          <div className="bg-fabre-lightgreen/20 rounded-xl p-6 border border-fabre-green/20">
            <p className="text-fabre-darkteal font-medium">
              💡 <strong>Conseil :</strong> Consultez nos autres départements d'intervention ou contactez-nous pour connaître nos solutions de proximité.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Trier les sites par ordre alphabétique pour une meilleure cohérence SEO
  const sortedSites = [...sites].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-8">
      {/* En-tête de la liste avec microdata */}
      <div className="text-center mb-8">
        <p className="text-lg text-fabre-darkteal font-medium" itemScope itemType="https://schema.org/ItemList">
          <span itemProp="numberOfItems">{sites.length}</span> centre(s) de recyclage à votre service
        </p>
      </div>

      {/* Grille des sites avec optimisations SEO */}
      <div 
        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        itemScope 
        itemType="https://schema.org/ItemList"
        role="list"
        aria-label={`Liste de ${sites.length} centres de recyclage`}
      >
        {sortedSites.map((site, index) => (
          <div 
            key={site.id}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
            itemScope
            itemType="https://schema.org/LocalBusiness"
            itemProp="itemListElement"
            role="listitem"
          >
            {/* Microdata cachées pour le SEO */}
            <meta itemProp="position" content={String(index + 1)} />
            <meta itemProp="name" content={site.name} />
            <meta itemProp="description" content={site.description || `Centre de recyclage ${site.name} à ${site.city}`} />
            <meta itemProp="telephone" content={site.phone || ""} />
            <meta itemProp="email" content={site.email || ""} />
            <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
              <meta itemProp="streetAddress" content={site.address} />
              <meta itemProp="addressLocality" content={site.city} />
              <meta itemProp="postalCode" content={site.postalCode} />
              <meta itemProp="addressCountry" content="FR" />
            </div>
            
            <SiteCard site={site} />
          </div>
        ))}
      </div>

      {/* Footer de la liste avec informations complémentaires */}
      <div className="mt-12 text-center">
        <div className="bg-gradient-to-r from-fabre-lightgreen/20 to-fabre-beige/20 rounded-2xl p-8 border border-fabre-green/20">
          <h3 className="text-xl font-semibold text-fabre-gray mb-4">
            🚀 Vous ne trouvez pas ce que vous cherchez ?
          </h3>
          <p className="text-fabre-darkteal leading-relaxed max-w-2xl mx-auto">
            Notre équipe commerciale étudie constamment de nouvelles opportunités d'implantation. 
            N'hésitez pas à nous contacter pour discuter de vos besoins spécifiques ou pour connaître 
            nos solutions de collecte dans votre zone.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SitesList;
